import { useRef, useId } from 'react'
import { useLanguage } from '@context/LanguageContext'
import { useFocusTrap } from '@hooks/useFocusTrap'
import { useEscapeKey } from '@hooks/useEscapeKey'
import { useAutoFocus } from '@hooks/useAutoFocus'
import { cx } from '@utils/cx'
import styles from './ExitGameModal.module.css'
import baseStyles from '@styles/modalBase.module.css'
/**
 * ExitGameModal - Shared exit confirmation modal for all games
 *
 * @param {Function} onClose - Handler for "stay" action (also triggered by ESC)
 * @param {Function} onExit - Handler for "exit" action
 * @param {string} imageSrc - Decorative image source (avatar or game image)
 * @param {boolean} imageSquare - Whether image should use square aspect ratio (default: false)
 */
export default function ExitGameModal ({ onClose, onExit, imageSrc, imageSquare = false }) {
  const { t } = useLanguage()

  const messageRef = useRef(null)
  const modalRef = useRef(null)

  const titleId = useId()
  const descriptionId = useId()

  useFocusTrap(modalRef)
  useEscapeKey(onClose)
  useAutoFocus(messageRef)

  return (
    <div
      className={baseStyles.overlayBottomFixed}
      role='dialog'
      aria-modal='true'
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <div className={styles.exitGameModal} ref={modalRef}>
        <div className={baseStyles.bottomHandle} />

        {imageSrc && (
          <img
            src={imageSrc}
            alt=''
            role='presentation'
            className={cx(
              styles.avatar,
              imageSquare && styles.avatarSquare
            )}
            loading='lazy'
            decoding='async'
          />
        )}

        <div className={styles.message}>
          <h2
            id={titleId}
            ref={messageRef}
            tabIndex='-1'
          >
            {t.shared.exitGame.question}
          </h2>
          <p id={descriptionId} className={styles.warning}>
            {t.shared.exitGame.warning}
          </p>
        </div>

        <button
          className={styles.stayBtn}
          onClick={onClose}
          aria-label={t.shared.access.exitModal.stay}
        >
          {t.shared.exitGame.stay}
        </button>
        <button
          className={styles.exitBtn}
          onClick={onExit}
          aria-label={t.shared.access.exitModal.exit}
        >
          {t.shared.exitGame.exit}
        </button>
      </div>
    </div>
  )
}
